import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RunningThresholdJob } from 'custom_hooks/data_hooks/jobs/runningAlertThresholdJobs.shared';
import { usePopup } from 'custom_hooks/usePopup';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { UserDetails } from 'pages/Desk/issues/UserDetails';
import { useRef } from 'react';
import { HoverTooltip } from 'shared/hover_tooltip/HoverTooltip';
import { TriggeredPopup } from 'shared/triggered_popup/TriggeredPopup';
import { getItemCountText } from 'utils/string';
import {
    HoverRef,
    HoverSpan,
    IconRow,
    SecondaryText,
    WarningSpan,
} from '../../AlarmThresholds/styled';

const DefaultVisibleRunningJobsCount = 3;

type RunningThresholdJobRowProps = Readonly<
    | {
          thresholdJob: Pick<RunningThresholdJob, 'fullName' | 'user' | 'enqueuedTime'>;
          displayTime?: never;
      }
    | {
          thresholdJob: Pick<RunningThresholdJob, 'fullName' | 'user'>;
          displayTime: Date;
      }
>;

export function RunningThresholdJobRow(props: RunningThresholdJobRowProps) {
    const nameAnchorRef = useRef<HTMLSpanElement>(null);
    const { formatDateTime } = useUserDateTimeFormat();
    let formattedTime: string;

    if ('enqueuedTime' in props.thresholdJob) {
        formattedTime = formatDateTime(props.thresholdJob.enqueuedTime);
    } else if (props.displayTime) {
        formattedTime = formatDateTime(props.displayTime);
    } else {
        return null;
    }

    return (
        <SecondaryText>
            <span>{formattedTime} by </span>
            <HoverSpan ref={nameAnchorRef}>{props.thresholdJob.fullName}</HoverSpan>
            <HoverTooltip anchorRef={nameAnchorRef} noPadding stickyHover>
                <UserDetails user={props.thresholdJob.user} />
            </HoverTooltip>
        </SecondaryText>
    );
}

export type ThresholdJobsRunningWarningProps = Readonly<{
    runningThresholdJobs?: RunningThresholdJob[];
    runningThresholdJobsError?: boolean;
    visibleJobs?: number;
    verbose?: boolean;
}>;

export function ThresholdJobsRunningWarning({
    runningThresholdJobs,
    runningThresholdJobsError,
    visibleJobs = DefaultVisibleRunningJobsCount,
    verbose = false,
}: ThresholdJobsRunningWarningProps) {
    const { triggerRef, popupRef, isOpen, toggleOpen, close } =
        usePopup<HTMLSpanElement>();

    const visibleRunningThresholdJobs = (runningThresholdJobs ?? []).slice(
        0,
        visibleJobs,
    );
    const hiddenRunningThresholdJobs = (runningThresholdJobs ?? []).slice(visibleJobs);
    const runningJobsCount = runningThresholdJobs?.length ?? 0;

    return (
        <>
            {runningThresholdJobsError && (
                <IconRow className="mt-10">
                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                    <span>Failed to load running threshold jobs</span>
                </IconRow>
            )}
            {runningJobsCount > 0 && (
                <IconRow className="mt-10">
                    <WarningSpan>
                        <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                    </WarningSpan>
                    <div>
                        <WarningSpan>
                            {getItemCountText(
                                runningJobsCount,
                                verbose
                                    ? 'alarm generation job is running at this location'
                                    : 'previous job is currently running:',
                                verbose
                                    ? 'alarm generation jobs are running at this location'
                                    : 'previous jobs are currently running:',
                            )}
                        </WarningSpan>
                        <div>
                            {visibleRunningThresholdJobs.map((thresholdJob, index) => (
                                <div key={thresholdJob.id}>
                                    <RunningThresholdJobRow thresholdJob={thresholdJob} />
                                    {index === visibleRunningThresholdJobs.length - 1 &&
                                        hiddenRunningThresholdJobs.length > 0 && (
                                            <SecondaryText>
                                                {' '}
                                                <HoverRef
                                                    ref={triggerRef}
                                                    onClick={toggleOpen}
                                                    className="underline"
                                                >
                                                    (+{hiddenRunningThresholdJobs.length}{' '}
                                                    more)
                                                </HoverRef>
                                            </SecondaryText>
                                        )}
                                </div>
                            ))}
                            {hiddenRunningThresholdJobs.length > 0 && isOpen && (
                                <TriggeredPopup
                                    anchorRef={triggerRef}
                                    ref={popupRef}
                                    onClose={close}
                                    test-id="GenerateAlarmThresholdsModal__RunningThresholdJobsPopup"
                                >
                                    {hiddenRunningThresholdJobs.map((thresholdJob) => (
                                        <div key={thresholdJob.id}>
                                            <RunningThresholdJobRow
                                                thresholdJob={thresholdJob}
                                            />
                                        </div>
                                    ))}
                                </TriggeredPopup>
                            )}
                        </div>
                    </div>
                </IconRow>
            )}
        </>
    );
}
